import { useRef, type KeyboardEvent } from 'react'
import type { Assessment, MyWorkItem, Vendor } from '../../lib/api'
import { flags, tabStorage, useFlag } from '../../lib/flags'
import type { VendorLifecycle } from '../../lib/lifecycle'
import { statutoryNote } from '../../lib/requestRules'
import { MiniTrack } from '../kit'
import { Icon } from '../ui'
import { lifecycleWord } from './facts'
import { LifecycleTrack, lifecycleStages, standing, useLifecycle, type LifecycleFacts } from './LifecycleTrack'
import { FittedSteps, NextStepBar, useVendorWork } from './NextStep'
import { nextSteps, withMyWork, type Step } from './nextSteps'
import type { Place } from './sections'

// Where the vendor stands and what waits on it, as one line above every section.
//
// # WHY ONE LINE
//
// The lifecycle track and the Next step panel were two cards, 105 px and up to
// 180 px, drawn above all eight sections of the record. The track changes a
// handful of times in a vendor's life and the panel's sentences are read once;
// the buttons are what a reader comes back for. So the line keeps the five
// stages as dots with the stage and its date in words, the count of steps that
// are the reader's, each step with which one it is and its button, and Details
// opens the full track and the sentences under it.
//
// # WHAT MAY NOT GO BEHIND DETAILS
//
// Anything that needs a person: the count, each action, and a step past its
// date, which is drawn with the late chip's clock. A suspended, rejected or
// offboarding vendor is said in words on the line, as well as on the stage
// chip in the header.
//
// # THREE PIECES, SO EACH CAN BE TESTED
//
// NowLine reads the lifecycle and the vendor's My work rows. NowLineOf works
// out the steps from them and reads whether Details is open. NowLineView draws
// what it is given and calls no hook, so a test can press its button and its
// Escape without a browser (test/vendor-record-head.test.mjs).

// The vendors whose Details are open. Outside the component, because a change
// of address can remount the record and it came back shut, and kept for the
// tab, so a reload finds it as the reader left it.
const tab = tabStorage()
const opened = flags(tab && { in: tab, as: 'tprm.vendor.details' })

// The open part's id, which the Details button names while it is open.
function fullId(vendorId: string): string {
  return 'vd-now-full-' + vendorId
}

// Puts the reader on a step under Details: on its button, or on its row where
// it has none. Found by the step's own key, not by a selector built from it,
// because a key holds an address and its punctuation.
function focusStep(card: Element | null, key: string) {
  const row = Array.from(card?.querySelectorAll<HTMLElement>('[data-step]') ?? []).find((el) => el.getAttribute('data-step') === key)
  ;(row?.querySelector<HTMLElement>('a[href], button:not([disabled])') ?? row)?.focus()
}

type ViewProps = LifecycleFacts & {
  vendorId: string
  steps: Step[]
  open: boolean
  // The words for the polite live region when the lifecycle has just moved.
  announce?: string
  // One quiet line under the line, where the vendor has one (C7 of the 12 Oct
  // plan: statutory details missing on a vendor still in draft).
  note?: string | null
  onGo: (p: Place) => void
  onChanged: () => void
}

export function NowLineView({ vendorId, steps, open, announce, note, onGo, onChanged, ...life }: ViewProps) {
  // Escape closes what Details opened and hands focus back to it, from the
  // button or from anywhere inside the open part.
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Escape' || !opened.has(vendorId)) return
    opened.set(vendorId, false)
    e.currentTarget.querySelector<HTMLElement>('.vd-line-more')?.focus()
  }
  const at = standing(life)
  return (
    <section className="vd-now" aria-label="Lifecycle and next steps" onKeyDown={onKey}>
      <div className="vd-line">
        <p className="vd-line-life">
          <span className="vd-line-stage">
            <MiniTrack states={lifecycleStages(life).map((s) => s.state)} label={at.dots} />
            {at.stage}
          </span>
          {at.more.map((fact, i) => (
            <span key={fact} className="vd-line-since">
              {fact}
              {i < at.more.length - 1 && ','}
            </span>
          ))}
        </p>
        <FittedSteps
          steps={steps}
          onGo={onGo}
          onChanged={onChanged}
          acts={!open}
          // The note that some steps are in Details opens them and puts the
          // reader on the first of those; a detail the line cut short does
          // the same for its own step. What was pressed leaves the page as
          // Details opens, and focus left with it went to the top of the
          // document. Once the open part is drawn: this press redraws it
          // before any microtask runs.
          onMore={(step, from) => {
            const card = from.closest('.vd-now')
            opened.set(vendorId, true)
            queueMicrotask(() => focusStep(card, step))
          }}
        />
        {/* The part it controls is in the page only while open, so it is named only then. */}
        <button
          type="button"
          className="ghost vd-line-more"
          aria-expanded={open}
          aria-controls={open ? fullId(vendorId) : undefined}
          onClick={() => opened.set(vendorId, !opened.has(vendorId))}
        >
          Details
          <Icon name="chevron" size={14} />
        </button>
      </div>
      {note && <p className="vd-quiet vd-line-note">{note}</p>}
      {open && (
        <div className="vd-now-full" id={fullId(vendorId)}>
          <LifecycleTrack {...life} />
          {/* With nothing waiting the line has already said so. */}
          {steps.length > 0 && <NextStepBar steps={steps} onGo={onGo} onChanged={onChanged} />}
        </div>
      )}
      <p className="vh" aria-live="polite">
        {announce ?? ''}
      </p>
    </section>
  )
}

type OfProps = Pick<LifecycleFacts, 'liveExit' | 'signOffs' | 'exitStage' | 'names'> & {
  vendor: Vendor
  assessments: Assessment[]
  onGo: (p: Place) => void
  onChanged: () => void
}

export function NowLineOf({
  vendor,
  assessments,
  work,
  life,
  onGo,
  onChanged,
  ...exit
}: OfProps & { work: MyWorkItem[] | null; life: VendorLifecycle | null }) {
  const open = useFlag(opened, vendor.id)
  const first = useRef(vendor.status)
  return (
    <NowLineView
      {...exit}
      vendorId={vendor.id}
      status={vendor.status}
      requestApproved={vendor.requestApproved}
      note={statutoryNote(vendor)}
      life={life}
      steps={withMyWork(nextSteps(vendor, assessments), work)}
      open={open}
      announce={vendor.status !== first.current ? 'Lifecycle now ' + lifecycleWord(vendor.status).word : undefined}
      onGo={onGo}
      onChanged={onChanged}
    />
  )
}

// The two reads the line is drawn from. They are a parameter so that a test
// can stand in for the requests and see that what was read reaches the line.
const READS = { useLife: useLifecycle, useWork: useVendorWork }

export function NowLine({ reads = READS, ...props }: OfProps & { reads?: typeof READS }) {
  const life = reads.useLife(props.vendor.id, props.vendor.status)
  const work = reads.useWork(props.vendor.id)
  return <NowLineOf {...props} life={life} work={work} />
}
